import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Empty, Input, Pagination, Space, Table, Tag, Typography, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { ExportOutlined, ReloadOutlined } from "@ant-design/icons";
import { useGlobal } from "../../contexts/GlobalContext";
import { useCurrentProject } from "../../hooks/useCurrentProject";
import request from "../../utils/request";

const { Text } = Typography;

interface ConversationRow {
  conversationId: string;
  title: string;
  intent?: string;
  status: string;
  pageUrl?: string;
  startedAt: string;
  durationMs: number;
  activeMs: number;
  runCount: number;
  frameCount: number;
  pageCount: number;
  messageCount: number;
}

function formatDuration(value: number) {
  if (!Number.isFinite(value) || value <= 0) return "0 秒";
  if (value < 60_000) return `${Math.max(1, Math.round(value / 1000))} 秒`;
  return `${Math.floor(value / 60_000)} 分 ${Math.round((value % 60_000) / 1000)} 秒`;
}

const ProjectCopilotReplay: React.FC = () => {
  const { API_BASE_URL } = useGlobal();
  const { project } = useCurrentProject();
  const projectId = project?.id;
  const [rows, setRows] = useState<ConversationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [launching, setLaunching] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState("");

  const load = useCallback(async (nextPage: number, nextSize: number) => {
    if (!projectId) return;
    setLoading(true);
    try {
      const response = await request.get(`${API_BASE_URL}/api/v3/projects/${projectId}/replay-conversations`, {
        params: { page: nextPage, pageSize: nextSize },
      });
      if (response.data?.code !== 200) throw new Error(response.data?.msg || "读取失败");
      const data = response.data?.data;
      setRows(data?.items || data?.list || data || []);
      setTotal(response.data?.total ?? data?.total ?? 0);
    } catch (error: any) {
      message.error(error?.response?.data?.msg || error.message || "读取会话回放失败");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, projectId]);

  useEffect(() => {
    setPage(1);
    setPageSize(20);
    void load(1, 20);
  }, [load]);

  const launch = useCallback(async (conversationId: string) => {
    if (!projectId || launching) return;
    // Open synchronously so browser popup protection does not block the
    // authenticated launch request that follows.
    const replayWindow = window.open("about:blank", "_blank");
    setLaunching(conversationId);
    try {
      const response = await request.post(
        `${API_BASE_URL}/api/v3/projects/${projectId}/replay-conversations/${encodeURIComponent(conversationId)}/launch`
      );
      const url = response.data?.data?.url;
      if (response.data?.code !== 200 || !url) throw new Error(response.data?.msg || "创建回放链接失败");
      if (replayWindow) {
        replayWindow.opener = null;
        replayWindow.location.replace(url);
      } else {
        window.location.assign(url);
      }
    } catch (error: any) {
      replayWindow?.close();
      message.error(error?.response?.data?.msg || error.message || "打开原站回放失败");
    } finally {
      setLaunching("");
    }
  }, [API_BASE_URL, launching, projectId]);

  const visibleRows = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return rows;
    return rows.filter((row) => [row.title, row.intent, row.pageUrl, row.conversationId]
      .some((value) => String(value || "").toLowerCase().includes(keyword)));
  }, [query, rows]);

  const columns: ColumnsType<ConversationRow> = [
    {
      title: "会话",
      key: "conversation",
      render: (_, row) => (
        <div className="copilot-replay-list-title">
          <Text strong ellipsis>{row.title || row.intent || "AI 操作会话"}</Text>
          <Text type="secondary" ellipsis>{row.pageUrl || row.conversationId}</Text>
        </div>
      ),
    },
    { title: "开始时间", dataIndex: "startedAt", width: 180, render: (value: string) => new Date(value).toLocaleString("zh-CN") },
    {
      title: "范围",
      key: "scope",
      width: 220,
      render: (_, row) => <Space size={4} wrap><Tag>{row.pageCount} 个页面</Tag><Tag>{row.runCount} 次运行</Tag><Tag>{row.messageCount} 条对话</Tag></Space>,
    },
    {
      title: "有效操作",
      key: "playback",
      width: 180,
      render: (_, row) => <div className="copilot-replay-list-title"><Text>{row.frameCount} 步 · {formatDuration(row.activeMs)}</Text><Text type="secondary">空闲片段已自动跳过</Text></div>,
    },
    {
      title: "状态",
      dataIndex: "status",
      width: 100,
      render: (value: string) => <Tag color={value === "failed" ? "error" : "success"}>{value === "failed" ? "失败" : "成功"}</Tag>,
    },
    {
      title: "",
      key: "action",
      width: 150,
      align: "right",
      render: (_, row) => (
        <Button type="primary" icon={<ExportOutlined />} loading={launching === row.conversationId} disabled={!row.conversationId || Boolean(launching)} onClick={() => void launch(row.conversationId)}>
          在原站回放
        </Button>
      ),
    },
  ];

  if (!projectId) return null;
  return (
    <div className="copilot-replay-list">
      <Alert
        type="info"
        showIcon
        message="在站长原始网站中回放"
        description="回放会打开原始页面，用只读高亮和模拟鼠标还原完整会话及跨页操作；不会执行真实点击、输入或提交。链接仅当前站点可用，10 分钟后自动失效。"
      />
      <div className="copilot-replay-list-toolbar">
        <Input.Search allowClear value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索任务、页面或会话 ID" style={{ maxWidth: 360 }} />
        <Button icon={<ReloadOutlined />} loading={loading} onClick={() => void load(page, pageSize)}>刷新</Button>
      </div>
      <Table<ConversationRow> rowKey="conversationId" columns={columns} dataSource={visibleRows} loading={loading} pagination={false} locale={{ emptyText: <Empty description="暂无可回放的 AI 操作会话" /> }} scroll={{ x: 1000 }} />
      <Pagination
        className="copilot-replay-pagination"
        current={page}
        pageSize={pageSize}
        total={total}
        showSizeChanger
        showTotal={(value) => `共 ${value} 个会话`}
        onChange={(nextPage, nextSize) => { setPage(nextPage); setPageSize(nextSize); void load(nextPage, nextSize); }}
      />
    </div>
  );
};

export default ProjectCopilotReplay;
